<template>
  <GoalEditDialog 
    v-if="editDialog.visible" 
    :goal="editDialog.goal"
    :owner-type="ownerType"
    :owner-idx="ownerIdx"
    :editDialogSwitch="editDialogSwitch"
    :reloadForSync="loadGoals"
  />

  <!-- 진행률 업데이트 바텀시트 -->
  <div class="progress-sheet" v-if="progressSheet.visible" @click.self="closeProgressSheet">
    <div class="progress-sheet-paper">
      <div class="sheet-handle"></div>
      <div class="sheet-header">
        <h3>{{ progressSheet.goal?.title }}</h3>
        <p class="sheet-sub">현재 {{ formatValue(progressSheet.goal?.current_value || 0, progressSheet.goal?.goal_type) }} / {{ formatValue(progressSheet.goal?.value, progressSheet.goal?.goal_type) }}{{ progressSheet.goal?.unit }}</p>
      </div>

      <!-- 빠른 추가 버튼들 -->
      <div class="quick-add-row">
        <button 
          v-for="q in quickAddValues" 
          :key="q"
          class="quick-add-btn"
          @click="quickAdd(q)"
        >
          +{{ q }}
        </button>
      </div>

      <!-- 직접 입력 -->
      <div class="custom-add-row">
        <input 
          v-model.number="progressSheet.customValue" 
          type="number" 
          placeholder="직접 입력"
          class="custom-input"
          @keyup.enter="applyCustomValue"
        />
        <button class="custom-apply-btn" @click="applyCustomValue">적용</button>
      </div>

      <!-- 또는 직접 세팅 -->
      <div class="set-value-row">
        <small>현재 값을 직접 설정</small>
        <div class="set-input-group">
          <input 
            v-model.number="progressSheet.setValue"
            type="number"
            placeholder="현재 값"
            class="set-input"
            @keyup.enter="applySetValue"
          />
          <span class="set-unit">{{ progressSheet.goal?.unit }}</span>
          <button class="set-apply-btn" @click="applySetValue">설정</button>
        </div>
      </div>
    </div>
  </div>

  <div id="dialog-goal-list" class="dialog">
    <span class="cls" @click="oth_showGoalListVisible(false)"></span>
    <div class="paper">
      <!-- 헤더 -->
      <header>
        <div class="header-left">
          <h1>🎯 목표</h1>
          <span class="goal-count-badge" v-if="goals.length">{{ activeCount }}개 진행중</span>
        </div>
        <button class="close" @click="oth_showGoalListVisible(false)">
          <i class="ph-bold ph-x"></i>
        </button>
      </header>

      <!-- 요약 카드 -->
      <div class="summary-strip">
        <div class="summary-item">
          <span class="summary-num accent">{{ activeCount }}</span>
          <span class="summary-label">진행중</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-item">
          <span class="summary-num success">{{ completedCount }}</span>
          <span class="summary-label">달성</span>
        </div>
        <div class="summary-divider"></div>
        <div class="summary-item">
          <span class="summary-num">{{ overallPercent }}%</span>
          <span class="summary-label">평균 진행률</span>
        </div>
      </div>

      <!-- 필터 탭 -->
      <nav class="tabs">
        <button :class="{ active: filter.status === '' }" @click="setFilter('')">전체</button>
        <button :class="{ active: filter.status === 'active' }" @click="setFilter('active')">진행중</button>
        <button :class="{ active: filter.status === 'completed' }" @click="setFilter('completed')">완료</button>
        <button :class="{ active: filter.status === 'paused' }" @click="setFilter('paused')">일시정지</button>
      </nav>

      <!-- 목표 리스트 -->
      <main>
        <div v-if="loading" class="loading">
          <div class="spinner"></div>
        </div>

        <div v-else-if="goals.length === 0" class="empty">
          <div class="empty-illustration">
            <span class="empty-target">🎯</span>
          </div>
          <p class="empty-title">아직 목표가 없어요</p>
          <p class="empty-desc">장기적인 목표를 설정하고<br/>하나씩 정복해보세요!</p>
          <button @click="openEdit(null)">+ 첫 목표 만들기</button>
        </div>

        <ul v-else>
          <li 
            v-for="goal in goals" 
            :key="goal.idx"
            :class="{ 
              done: isDone(goal),
              focused: goal.idx === focusedGoalIdx,
              paused: goal.status === 'paused'
            }"
          >
            <!-- 상단: 제목 + 디데이 -->
            <div class="goal-top" @click="openEdit(goal)">
              <div class="goal-title-area">
                <span class="goal-status-dot" :class="getStatusClass(goal)"></span>
                <h3>{{ goal.title }}</h3>
              </div>
              <div class="goal-badges">
                <span class="dday" :class="getDaysLeftClass(goal)">
                  {{ getDaysLeftText(goal) }}
                </span>
              </div>
            </div>

            <!-- 프로그레스 바 -->
            <div class="goal-progress" @click="openEdit(goal)">
              <div class="progress-bar-bg">
                <div 
                  class="progress-bar-fill" 
                  :class="getProgressClass(goal)"
                  :style="{ width: getPercent(goal) + '%' }"
                ></div>
              </div>
              <div class="progress-labels">
                <span class="progress-current">{{ formatValue(goal.current_value || 0, goal.goal_type) }}{{ goal.unit }}</span>
                <span class="progress-percent" :class="getProgressClass(goal)">{{ getPercent(goal) }}%</span>
                <span class="progress-target">{{ formatValue(goal.value, goal.goal_type) }}{{ goal.unit }}</span>
              </div>
            </div>

            <!-- 하단 액션 -->
            <div class="goal-actions">
              <button class="action-btn update-btn" @click.stop="openProgressSheet(goal)" v-if="!isDone(goal) && goal.status === 'active'">
                <i class="ph ph-plus-circle"></i>
                <span>기록</span>
              </button>
              <button 
                class="action-btn focus-btn" 
                :class="{ 'is-focused': goal.idx === focusedGoalIdx }"
                @click.stop="toggleFocus(goal)"
                v-if="goal.status === 'active'"
              >
                <i class="ph" :class="goal.idx === focusedGoalIdx ? 'ph-star-fill' : 'ph-star'"></i>
                <span>{{ goal.idx === focusedGoalIdx ? '집중중' : '집중' }}</span>
              </button>
              <button class="action-btn edit-btn" @click.stop="openEdit(goal)">
                <i class="ph ph-pencil-simple"></i>
                <span>수정</span>
              </button>
              <button class="action-btn delete-btn" @click.stop="confirmDelete(goal)">
                <i class="ph ph-trash"></i>
              </button>
            </div>

            <!-- 포커스 뱃지 -->
            <div class="focus-ribbon" v-if="goal.idx === focusedGoalIdx">
              ⚡ 집중 목표
            </div>

            <!-- 달성 축하 이펙트 -->
            <div class="done-overlay" v-if="isDone(goal)">
              <span>🏆 달성!</span>
            </div>
          </li>
        </ul>

        <!-- 동기부여 메시지 -->
        <div class="motivation-card" v-if="goals.length > 0 && !loading">
          <p>{{ motivationMessage }}</p>
        </div>
      </main>

      <!-- FAB -->
      <button class="fab" @click="openEdit(null)">
        <i class="ph-bold ph-plus"></i>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, inject, defineProps } from 'vue'
import GoalEditDialog from './goalEdit.vue'

const props = defineProps({
  ownerType: { type: String, default: 'member' },
  ownerIdx: { type: [Number, String], default: null },
  oth_showGoalListVisible: { type: Function, required: true }
})

const sql_query = inject('sql_query')
const getMbid = inject('getMbid')

const loading = ref(false)
const goals = ref([])
const filter = reactive({ status: '' })
const focusedGoalIdx = ref(null)

const editDialog = reactive({
  visible: false,
  goal: null
})

// 진행률 업데이트 시트
const progressSheet = reactive({
  visible: false,
  goal: null,
  customValue: null,
  setValue: null
})

const activeCount = computed(() => goals.value.filter(g => g.status === 'active').length)
const completedCount = computed(() => goals.value.filter(g => g.status === 'completed' || isDone(g)).length)
const overallPercent = computed(() => {
  const active = goals.value.filter(g => g.status === 'active' || g.status === 'completed')
  if (active.length === 0) return 0
  const total = active.reduce((sum, g) => sum + Math.min(getPercent(g), 100), 0)
  return Math.round(total / active.length)
})

// 동기부여 메시지
const motivationMessages = [
  '한 걸음씩, 꾸준히. 이미 시작한 것만으로 대단해요! 💪',
  '작은 진전이 모여 큰 성과가 됩니다 🚀',
  '오늘의 기록이 내일의 성장이 됩니다 ✨',
  '포기하지 않는 것, 그 자체가 승리예요 🏆',
  '목표를 향해 달리는 당신, 멋져요! 🔥'
]

const motivationMessage = computed(() => {
  if (completedCount.value > 0 && activeCount.value === 0) {
    return '🎉 모든 목표를 달성했어요! 새로운 도전을 시작해볼까요?'
  }
  if (overallPercent.value >= 80) {
    return '🔥 거의 다 왔어요! 마지막까지 파이팅!'
  }
  if (overallPercent.value >= 50) {
    return '💪 절반 이상 왔어요! 이 페이스면 충분해요.'
  }
  const idx = new Date().getDate() % motivationMessages.length
  return motivationMessages[idx]
})

// 빠른 추가 값 (goal_type에 따라)
const quickAddValues = computed(() => {
  const goal = progressSheet.goal
  if (!goal) return [1, 5, 10]
  if (goal.goal_type === 'amount') {
    if (goal.unit === '만원') return [10, 50, 100, 500]
    return [10000, 50000, 100000]
  }
  if (goal.goal_type === 'rate') return [5, 10, 25]
  return [1, 3, 5, 10]
})

onMounted(() => {
  loadGoals()
  // 로컬스토리지에서 포커스 목표 복원
  try {
    const saved = localStorage.getItem('focused_goal_idx')
    if (saved) focusedGoalIdx.value = Number(saved)
  } catch(e) { console.log(e) }
})

async function loadGoals() {
  loading.value = true
  try {
    let sql = `SELECT * FROM goals WHERE mb_id = '${getMbid()}' AND owner_type = '${props.ownerType}'`
    if (props.ownerIdx) sql += ` AND owner_idx = ${props.ownerIdx}`
    if (filter.status) sql += ` AND status = '${filter.status}'`
    sql += ` ORDER BY FIELD(status, 'active', 'paused', 'completed', 'cancelled'), created_at DESC`
    const result = await sql_query(sql)
    goals.value = Array.isArray(result) ? result : []
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

function setFilter(status) {
  filter.status = status
  loadGoals()
}

function editDialogSwitch(b) {
  editDialog.visible = b
  if (!b) editDialog.goal = null
}

function openEdit(goal) {
  editDialog.goal = goal
  editDialog.visible = true
}

async function confirmDelete(goal) {
  if (!confirm(`"${goal.title}" 삭제?`)) return
  try {
    await sql_query(`DELETE FROM goals WHERE idx = ${goal.idx}`)
    if (focusedGoalIdx.value === goal.idx) {
      focusedGoalIdx.value = null
      try { localStorage.removeItem('focused_goal_idx') } catch(e) { console.log(e) }
    }
    loadGoals()
    window.dispatchEvent(new Event('goals-updated'))
  } catch (e) {
    alert('삭제 실패')
  }
}

// ========== 포커스 ==========
function toggleFocus(goal) {
  if (focusedGoalIdx.value === goal.idx) {
    focusedGoalIdx.value = null
    try { localStorage.removeItem('focused_goal_idx') } catch(e) { console.log(e) }
  } else {
    focusedGoalIdx.value = goal.idx
    try { localStorage.setItem('focused_goal_idx', goal.idx) } catch(e) { console.log(e) }
  }
}

// ========== 진행률 업데이트 ==========
function openProgressSheet(goal) {
  progressSheet.goal = goal
  progressSheet.customValue = null
  progressSheet.setValue = goal.current_value || 0
  progressSheet.visible = true
}

function closeProgressSheet() {
  progressSheet.visible = false
  progressSheet.goal = null
}

async function updateProgress(goalIdx, newValue) {
  try {
    await sql_query(`UPDATE goals SET current_value = ${newValue}, updated_at = NOW() WHERE idx = ${goalIdx}`)
    // 로컬 데이터도 즉시 반영
    const g = goals.value.find(x => x.idx === goalIdx)
    if (g) g.current_value = newValue
    // 자동 완료 체크
    if (g && newValue >= g.value && g.status === 'active') {
      if (confirm(`🏆 "${g.title}" 목표를 달성했어요! 완료로 변경할까요?`)) {
        await sql_query(`UPDATE goals SET status = 'completed' WHERE idx = ${goalIdx}`)
        g.status = 'completed'
      }
    }
    window.dispatchEvent(new Event('goals-updated'))
  } catch(e) {
    alert('업데이트 실패')
    console.error(e)
  }
}

async function quickAdd(amount) {
  if (!progressSheet.goal) return
  const current = Number(progressSheet.goal.current_value || 0)
  const newVal = current + amount
  await updateProgress(progressSheet.goal.idx, newVal)
  progressSheet.setValue = newVal
}

async function applyCustomValue() {
  if (!progressSheet.goal || !progressSheet.customValue) return
  const current = Number(progressSheet.goal.current_value || 0)
  const newVal = current + progressSheet.customValue
  await updateProgress(progressSheet.goal.idx, newVal)
  progressSheet.setValue = newVal
  progressSheet.customValue = null
}

async function applySetValue() {
  if (!progressSheet.goal || progressSheet.setValue == null) return
  await updateProgress(progressSheet.goal.idx, progressSheet.setValue)
}

// ========== 유틸 ==========
function isDone(goal) {
  if (goal.status === 'completed') return true
  if (!goal.value) return false
  return (goal.current_value || 0) >= goal.value
}

function getPercent(goal) {
  if (!goal.value || goal.value === 0) return 0
  return Math.min(100, Math.round(((goal.current_value || 0) / goal.value) * 100))
}

function getProgressClass(goal) {
  const p = getPercent(goal)
  if (isDone(goal)) return 'complete'
  if (p >= 70) return 'high'
  if (p >= 40) return 'mid'
  return 'low'
}

function getStatusClass(goal) {
  if (isDone(goal)) return 'status-done'
  if (goal.status === 'paused') return 'status-paused'
  if (goal.status === 'cancelled') return 'status-cancelled'
  return 'status-active'
}

function formatValue(val, type) {
  if (type === 'amount') return Number(val || 0).toLocaleString()
  return val || 0
}

function getDaysLeft(goal) {
  const end = new Date(goal.end_date)
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  end.setHours(0, 0, 0, 0)
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24))
}

function getDaysLeftText(goal) {
  if (isDone(goal)) return '달성 ✓'
  if (goal.status === 'paused') return '일시정지'
  const d = getDaysLeft(goal)
  if (d > 0) return `D-${d}`
  if (d === 0) return 'D-Day'
  return '기한 초과'
}

function getDaysLeftClass(goal) {
  if (isDone(goal)) return 'done'
  if (goal.status === 'paused') return 'paused'
  const d = getDaysLeft(goal)
  if (d <= 0) return 'expired'
  if (d <= 7) return 'urgent'
  return ''
}
</script>

<style scoped>
#dialog-goal-list {
  position: absolute;
  inset: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--z-index-top);
  background: var(--color-surface-deep);
  backdrop-filter: blur(var(--effect-blur));
  padding: 16px;
  box-sizing: border-box;
}

.cls {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.paper {
  width: 100%;
  max-width: 480px;
  height: 100%;
  max-height: 90vh;
  background: var(--color-background);
  border-radius: 20px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* 헤더 */
header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px 12px;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

header h1 {
  font-size: var(--font-size-xl);
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.5px;
}

.goal-count-badge {
  padding: 4px 10px;
  background: rgba(255, 165, 0, 0.15);
  color: var(--color-brand);
  border-radius: 12px;
  font-size: var(--font-size-xs);
  font-weight: 600;
}

header .close {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--color-surface-reverse);
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-primary);
  font-size: var(--font-size-md);
  transition: transform 0.2s;
}

header .close:hover {
  transform: scale(1.1);
}

/* 요약 스트립 */
.summary-strip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  padding: 16px 24px;
  margin: 0 24px;
  background: var(--color-background-light);
  border-radius: 14px;
  flex-shrink: 0;
}

.summary-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.summary-divider {
  width: 1px;
  height: 32px;
  background: var(--color-text-third);
}

.summary-num {
  font-size: var(--font-size-xl);
  font-weight: 800;
  color: var(--color-text-primary);
}

.summary-num.accent {
  color: var(--color-brand);
}

.summary-num.success {
  color: #22c55e;
}

.summary-label {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* 탭 */
.tabs {
  display: flex;
  gap: 6px;
  padding: 16px 24px 12px;
  flex-shrink: 0;
  overflow-x: auto;
}

.tabs button {
  padding: 8px 16px;
  border: none;
  background: none;
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-secondary);
  cursor: pointer;
  border-radius: 20px;
  transition: all 0.2s;
  white-space: nowrap;
  border: 1px solid var(--color-text-third);
}

.tabs button:hover {
  background: var(--color-surface-reverse);
}

.tabs button.active {
  background: var(--color-brand);
  color: #000;
  border-color: var(--color-brand);
}

/* 메인 */
main {
  flex: 1;
  overflow-y: auto;
  padding: 8px 24px 80px;
}

main::-webkit-scrollbar {
  width: 4px;
}

main::-webkit-scrollbar-thumb {
  background: var(--color-surface-reverse);
  border-radius: 2px;
}

/* 로딩 */
.loading {
  display: flex;
  justify-content: center;
  padding: 60px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--color-surface-reverse);
  border-top-color: var(--color-brand);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 빈 상태 */
.empty {
  text-align: center;
  padding: 48px 20px;
}

.empty-illustration {
  margin-bottom: 20px;
}

.empty-target {
  font-size: 64px;
  display: inline-block;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}

.empty-title {
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
  margin: 0 0 8px;
}

.empty-desc {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  margin: 0 0 24px;
  line-height: 1.5;
}

.empty button {
  padding: 12px 24px;
  border: 2px solid var(--color-brand);
  background: none;
  border-radius: 30px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  color: var(--color-brand);
  cursor: pointer;
  transition: all 0.2s;
}

.empty button:hover {
  background: var(--color-brand);
  color: #000;
}

/* ========== 목표 카드 ========== */
ul {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

li {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: var(--color-background-light);
  border: 1px solid var(--color-text-third);
  border-radius: 16px;
  position: relative;
  transition: all 0.2s;
  overflow: hidden;
}

li:hover {
  border-color: var(--color-text-secondary);
}

li.focused {
  border-color: var(--color-brand);
  box-shadow: 0 0 0 1px var(--color-brand);
}

li.done {
  border-color: #22c55e;
  background: rgba(34, 197, 94, 0.03);
}

li.paused {
  opacity: 0.6;
}

/* 상단: 제목 + 디데이 */
.goal-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  cursor: pointer;
}

.goal-title-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

.goal-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.goal-status-dot.status-active {
  background: var(--color-brand);
  box-shadow: 0 0 6px rgba(255, 165, 0, 0.5);
}

.goal-status-dot.status-done {
  background: #22c55e;
}

.goal-status-dot.status-paused {
  background: var(--color-text-secondary);
}

.goal-status-dot.status-cancelled {
  background: var(--color-special-warning);
}

.goal-top h3 {
  font-size: var(--font-size-base);
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--color-text-primary);
}

li.done .goal-top h3 {
  text-decoration: line-through;
  color: var(--color-text-secondary);
}

.goal-badges {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.dday {
  font-size: var(--font-size-xs);
  font-weight: 700;
  color: var(--color-text-secondary);
  padding: 3px 8px;
  background: var(--color-surface-reverse);
  border-radius: 8px;
}

.dday.urgent {
  color: var(--color-brand);
  background: rgba(255, 165, 0, 0.1);
}

.dday.expired {
  color: var(--color-special-warning);
  background: rgba(218, 47, 71, 0.1);
}

.dday.done {
  color: #22c55e;
  background: rgba(34, 197, 94, 0.1);
}

.dday.paused {
  color: var(--color-text-secondary);
}

/* 프로그레스 바 */
.goal-progress {
  cursor: pointer;
}

.progress-bar-bg {
  width: 100%;
  height: 8px;
  background: var(--color-surface-reverse);
  border-radius: 4px;
  overflow: hidden;
}

.progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
  min-width: 2px;
}

.progress-bar-fill.low {
  background: var(--color-text-secondary);
}

.progress-bar-fill.mid {
  background: var(--color-brand);
}

.progress-bar-fill.high {
  background: linear-gradient(90deg, var(--color-brand), #f97316);
}

.progress-bar-fill.complete {
  background: linear-gradient(90deg, #22c55e, #16a34a);
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 6px;
}

.progress-current {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--color-text-primary);
}

.progress-percent {
  font-size: var(--font-size-xs);
  font-weight: 700;
}

.progress-percent.low {
  color: var(--color-text-secondary);
}

.progress-percent.mid {
  color: var(--color-brand);
}

.progress-percent.high {
  color: #f97316;
}

.progress-percent.complete {
  color: #22c55e;
}

.progress-target {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

/* 하단 액션 */
.goal-actions {
  display: flex;
  gap: 6px;
  padding-top: 4px;
  border-top: 1px solid var(--color-surface-reverse);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border: none;
  background: none;
  border-radius: 8px;
  font-size: var(--font-size-xs);
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.15s;
}

.action-btn i {
  font-size: var(--font-size-sm);
}

.action-btn:hover {
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
}

.action-btn.update-btn {
  color: var(--color-brand);
  font-weight: 600;
}

.action-btn.update-btn:hover {
  background: rgba(255, 165, 0, 0.1);
}

.action-btn.focus-btn.is-focused {
  color: var(--color-brand);
}

.action-btn.delete-btn {
  margin-left: auto;
}

.action-btn.delete-btn:hover {
  color: var(--color-special-warning);
  background: rgba(218, 47, 71, 0.1);
}

/* 포커스 리본 */
.focus-ribbon {
  position: absolute;
  top: 0;
  right: 0;
  padding: 3px 12px 3px 16px;
  background: var(--color-brand);
  color: #000;
  font-size: 10px;
  font-weight: 700;
  border-radius: 0 16px 0 10px;
}

/* 달성 오버레이 */
.done-overlay {
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 12px;
  background: rgba(34, 197, 94, 0.15);
  border-radius: 10px;
  font-size: var(--font-size-xs);
  font-weight: 700;
  color: #22c55e;
}

/* 동기부여 카드 */
.motivation-card {
  margin-top: 20px;
  padding: 16px 20px;
  background: var(--color-background-light);
  border-radius: 14px;
  border: 1px dashed var(--color-text-third);
  text-align: center;
}

.motivation-card p {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: 1.5;
}

/* FAB */
.fab {
  position: absolute;
  bottom: 24px;
  right: 24px;
  width: 52px;
  height: 52px;
  border: none;
  background: var(--color-brand);
  border-radius: 50%;
  color: #000;
  font-size: var(--font-size-lg);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(255, 165, 0, 0.4);
  transition: all 0.2s;
  z-index: 10;
}

.fab:hover {
  transform: scale(1.1) rotate(90deg);
}

/* ========== 프로그레스 시트 ========== */
.progress-sheet {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 10000;
  display: flex;
  justify-content: center;
  align-items: flex-end;
}

.progress-sheet-paper {
  width: 100%;
  max-width: 480px;
  background: var(--color-background);
  border-radius: 20px 20px 0 0;
  padding: 12px 24px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: slideUp 0.25s ease;
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

.sheet-handle {
  width: 40px;
  height: 4px;
  background: var(--color-text-third);
  border-radius: 2px;
  margin: 0 auto;
}

.sheet-header h3 {
  margin: 0 0 4px;
  font-size: var(--font-size-lg);
  font-weight: 700;
  color: var(--color-text-primary);
}

.sheet-sub {
  margin: 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
}

.quick-add-row {
  display: flex;
  gap: 8px;
}

.quick-add-btn {
  flex: 1;
  padding: 14px 8px;
  border: 2px solid var(--color-text-third);
  background: none;
  border-radius: 12px;
  font-size: var(--font-size-base);
  font-weight: 700;
  color: var(--color-brand);
  cursor: pointer;
  transition: all 0.15s;
}

.quick-add-btn:hover {
  border-color: var(--color-brand);
  background: rgba(255, 165, 0, 0.1);
}

.quick-add-btn:active {
  transform: scale(0.95);
}

.custom-add-row {
  display: flex;
  gap: 8px;
}

.custom-input {
  flex: 1;
  padding: 12px 16px;
  border: 2px solid var(--color-text-third);
  border-radius: 12px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  font-weight: 600;
  box-sizing: border-box;
}

.custom-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.custom-apply-btn {
  padding: 12px 20px;
  border: none;
  background: var(--color-brand);
  color: #000;
  border-radius: 12px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.custom-apply-btn:hover {
  opacity: 0.9;
}

.set-value-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
  border-top: 1px solid var(--color-text-third);
}

.set-value-row small {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.set-input-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.set-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid var(--color-text-third);
  border-radius: 10px;
  background: var(--color-background);
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  box-sizing: border-box;
}

.set-input:focus {
  outline: none;
  border-color: var(--color-brand);
}

.set-unit {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  font-weight: 600;
}

.set-apply-btn {
  padding: 10px 16px;
  border: none;
  background: var(--color-surface-reverse);
  color: var(--color-text-primary);
  border-radius: 10px;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
}

.set-apply-btn:hover {
  background: var(--color-text-third);
}

/* 모바일 */
@media (max-width: 768px) {
  .paper {
    max-height: 95vh;
    border-radius: 20px 20px 0 0;
  }

  li:hover .action-btn.delete-btn {
    opacity: 1;
  }
}
</style>